import axios from "axios";
import { compress } from "../helpers/common.helper";

const apiUri = import.meta.env.GATEWAY_URI;

export default function useWorks() {
  const queryWorks = async (filter, page, limit, project, sort) => {
    const reqBody = {};

    if (filter) reqBody.filter = filter;
    if (page) reqBody.page = page;
    if (limit) reqBody.limit = limit;
    if (project) reqBody.project = project;
    if (sort) reqBody.sort = sort;

    const _body = compress(JSON.stringify(reqBody));

    const result = await axios.post(`${apiUri}/api/v1/works/query`, _body, {
      headers: {
        "Content-Type": "text/plain",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    return result.data;
  };

  const getWork = async (id) => {
    const result = await axios.get(`${apiUri}/api/v1/works/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return result.data?.data;
  };

  const createWork = async (body) => {
    const _body = compress(JSON.stringify(body));

    const result = await axios.post(`${apiUri}/api/v1/works`, _body, {
      headers: {
        "Content-Type": "text/plain",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    return result?.data.data;
  };

  const patchWork = async (workId, body) => {
    const _body = compress(JSON.stringify(body));

    const result = await axios.patch(
      `${apiUri}/api/v1/works/${workId}`,
      _body,
      {
        headers: {
          "Content-Type": "text/plain",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    return result?.data.data;
  };

  const uploadWorkPanoramaImage = async (workId, panoramaImageId, file) => {
    let data = new FormData();
    data.append("file", file, file.name);

    const result = await axios.patch(
      `${apiUri}/api/v1/works/${workId}/panorama/images/${panoramaImageId}/upload`,
      data,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          maxBodyLength: Infinity,
        },
      }
    );

    return result?.data.data;
  };

  const uploadBanner = async (workId, file) => {
    let data = new FormData();
    data.append("file", file, "cover.png");

    const result = await axios.patch(
      `${apiUri}/api/v1/works/${workId}/banner/upload`,
      data,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          maxBodyLength: Infinity,
        },
      }
    );

    return result?.data.data;
  };

  const uploadWorkFiles = async (workId, files) => {
    let data = new FormData();

    for (const image of files) {
      data.append("files", image, image.name);
    }

    const result = await axios.patch(
      `${apiUri}/api/v1/works/${workId}/files/upload`,
      data,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          maxBodyLength: Infinity,
        },
      }
    );

    return result?.data.data;
  };

  const patchHotspot = async (workId, panoramaImageId, hotspotId, body) => {
    const _body = compress(JSON.stringify(body));

    const result = await axios.patch(
      `${apiUri}/api/v1/works/${workId}/panorama/images/${panoramaImageId}/hotspots/${hotspotId}`,
      _body,
      {
        headers: {
          "Content-Type": "text/plain",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    return result?.data.data;
  };

  const uploadImages = async (workId, images, imagePosition = []) => {
    let data = new FormData();

    const position = compress(JSON.stringify(imagePosition));

    data.append("positions", position);

    for (const image of images) {
      data.append("files", image, image.name);
    }

    const result = await axios.patch(
      `${apiUri}/api/v1/works/${workId}/images/upload`,
      data,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          maxBodyLength: Infinity,
        },
      }
    );

    return result?.data.data;
  };

  const uploadFloorPlans = async (workId, floorPlanId, file) => {
    let data = new FormData();
    data.append("file", file, file.name);

    const result = await axios.patch(
      `${apiUri}/api/v1/works/${workId}/floorPlans/${floorPlanId}/upload`,
      data,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          maxBodyLength: Infinity,
        },
      }
    );

    return result?.data.data;
  };

  const uploadHotspotFile = async (workId, imageId, hotspotId, file) => {
    let data = new FormData();
    data.append("file", file, file.name);

    const result = await axios.patch(
      `${apiUri}/api/v1/works/${workId}/panorama/images/${imageId}/hotspots/${hotspotId}/file/upload`,
      data,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          maxBodyLength: Infinity,
        },
      }
    );

    return result?.data.data;
  };

  const uploadHotspotIcon = async (workId, imageId, hotspotId, file) => {
    let data = new FormData();
    data.append("file", file, file.name);

    const result = await axios.patch(
      `${apiUri}/api/v1/works/${workId}/panorama/images/${imageId}/hotspots/${hotspotId}/icon/upload`,
      data,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          maxBodyLength: Infinity,
        },
      }
    );

    return result?.data.data;
  };

  const addWorkView = async (workId, userId) => {
    const headers = {
      "Content-Type": "text/plain",
    };

    // * Add x-uid to header if specified
    if (userId) {
      headers["x-uid"] = userId;
    }

    const result = await axios.post(
      `${apiUri}/api/v1/works/${workId}/views`,
      null,
      {
        headers,
      }
    );

    return result?.data;
  };

  const addWorkDownload = async (workId) => {
    const result = await axios.post(
      `${apiUri}/api/v1/works/${workId}/downloads`,
      null,
      {
        headers: {
          "Content-Type": "text/plain",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    return result?.data.data;
  };

  const getWorkRating = async (id) => {
    const result = await axios.get(`${apiUri}/api/v1/works/${id}/rating`);
    return result.data?.data;
  };

  const deleteWork = async (id) => {
    const result = await axios.delete(`${apiUri}/api/v1/works/${id}`, {
      headers: {
        "Content-Type": "text/plain",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return result.data?.data;
  };

  const deleteWorkImage = async (id, imageId) => {
    const result = await axios.delete(
      `${apiUri}/api/v1/works/${id}/images/${imageId}`,
      {
        headers: {
          "Content-Type": "text/plain",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    return result.data?.data;
  };

  const getFileWork = async (workId) => {
    const result = await axios.get(
      `${apiUri}/api/v1/works/${workId}/downloads`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    return result.data?.data;
  };

  const uploadWorkWatermark = async (workId, file) => {
    let data = new FormData();
    data.append("file", file, "cover.png");

    const result = await axios.patch(
      `${apiUri}/api/v1/works/${workId}/watermark/upload`,
      data,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          maxBodyLength: Infinity,
        },
      }
    );

    return result?.data.data;
  };

  const uploadPanoramaBanner = async (workId, panoramaImageId, file) => {
    let data = new FormData();
    data.append("file", file, "cover.png");

    const result = await axios.patch(
      `${apiUri}/api/v1/works/${workId}/panorama/images/${panoramaImageId}/banner/upload`,
      data,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          maxBodyLength: Infinity,
        },
      }
    );

    return result?.data.data;
  };

  const queryWorkRatings = async (filter, page, limit, project, sort) => {
    const reqBody = {};

    if (filter) reqBody.filter = filter;
    if (page) reqBody.page = page;
    if (limit) reqBody.limit = limit;
    if (project) reqBody.project = project;
    if (sort) reqBody.sort = sort;

    const _body = compress(JSON.stringify(reqBody));

    const result = await axios.post(
      `${apiUri}/api/v1/works/ratings/query`,
      _body,
      {
        headers: {
          "Content-Type": "text/plain",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    return result.data;
  };

  return {
    queryWorks,
    getWork,
    createWork,
    patchWork,
    uploadWorkPanoramaImage,
    uploadBanner,
    patchHotspot,
    uploadImages,
    uploadFloorPlans,
    uploadHotspotFile,
    uploadHotspotIcon,
    addWorkView,
    addWorkDownload,
    getWorkRating,
    uploadWorkFiles,
    deleteWork,
    deleteWorkImage,
    getFileWork,
    uploadWorkWatermark,
    uploadPanoramaBanner,
    queryWorkRatings,
  };
}
